// The business's brand kit (settings: brand.colours, brand.fonts, brand.logo).
// - brandTokens(): the brand's accent where it is legible, for what the business shows others (the playbook deck and
//   its PowerPoint). Never reminder, done or danger, and never a colour that looks like them.
// - applyBrand(): white-labelling the app. Off by default: the app is Ogma (warm paper ground, an ink spine with one
//   seal red notch, seal red actions). With app_theme.white_label the business's colour replaces the left-hand bar
//   only, and its logo goes on it; the main area stays Ogma's paper with seal red actions.
import type { JsonObject } from "./types";
import { FIXED, INPUTS, INK, mix, onColour, onSpine, resolveTheme, BONE } from "./theme";

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseHex(v: unknown): [number, number, number] | null {
  if (typeof v !== "string") return null;
  const m = HEX.exec(v.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function luminance([r, g, b]: [number, number, number]): number {
  const ch = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

export function contrast(a: string, b: string): number {
  const x = parseHex(a);
  const y = parseHex(b);
  if (!x || !y) return 0;
  const [l1, l2] = [luminance(x), luminance(y)].sort((p, q) => q - p) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

const norm = (v: string) => (v.startsWith("#") ? v : `#${v}`).toLowerCase();

/** Surfaces the accent has to read on: the Ogma panels and ground in each theme (theme.ts). */
const LIGHT = resolveTheme("light");
const DARK = resolveTheme("dark");
export const LIGHT_SURFACE = LIGHT.surface!;
export const DARK_SURFACE = DARK.surface!;

/** Colours a brand may not imitate: ochre for waiting on you (both themes), moss for done, red for errors. */
const RESERVED = [INPUTS.light.reminder, INPUTS.dark.reminder, FIXED.light.done, FIXED.dark.done, FIXED.light.danger, FIXED.dark.danger];

function hsl(c: string): [number, number, number] {
  const [r, g, b] = parseHex(c)!.map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) * 60 : max === g ? ((b - r) / d + 2) * 60 : ((r - g) / d + 4) * 60;
  return [h, s, l];
}

/** Too close to a reserved colour: a similar hue at a similar strength reads as the same meaning. */
export function looksReserved(c: string): boolean {
  const [h, s, l] = hsl(c);
  if (s < 0.2) return false;
  return RESERVED.some((r) => {
    const [rh, rs, rl] = hsl(r);
    const dh = Math.min(Math.abs(h - rh), 360 - Math.abs(h - rh));
    return rs >= 0.2 && dh < 22 && Math.abs(l - rl) < 0.18;
  });
}

function legibleOn(c: string, theme: Record<string, string>): boolean {
  return contrast(c, theme.surface!) >= 4.5 && contrast(c, theme.background!) >= 4.5 && contrast(onColour(c), c) >= 4.5;
}

/** The light brand colour lifted toward white until it reads on the dark panels, keeping its hue. */
function liftForDark(c: string): string | null {
  for (let white = 20; white <= 85; white += 5) {
    const lifted = mix("#ffffff", white, c);
    if (legibleOn(lifted, DARK) && !looksReserved(lifted)) return lifted;
  }
  return null;
}

export interface BrandTokens {
  accentLight: string | null;
  accentDark: string | null;
  /** 0.13 brand.colours.action (buttons) where it reads on light panels, else the accent. */
  actionLight: string | null;
  /** 0.13 brand.colours.reminder as a fill with ink text (4.5:1), else Ogma's amber. */
  reminder: string;
  headingFont: string | null;
  bodyFont: string | null;
}

export function brandTokens(config: JsonObject | null | undefined): BrandTokens {
  const brand = (config?.brand ?? {}) as JsonObject;
  const colours = (brand.colours ?? {}) as JsonObject;
  const fonts = (brand.fonts ?? {}) as JsonObject;
  const candidates = (keys: string[]) => keys.map((k) => colours[k]).filter((v): v is string => typeof v === "string" && Boolean(parseHex(v))).map(norm);
  const pick = (keys: string[], theme: Record<string, string>) => candidates(keys).find((c) => legibleOn(c, theme) && !looksReserved(c)) ?? null;
  const font = (v: unknown) => (typeof v === "string" && /^[A-Za-z0-9 ]{2,40}$/.test(v.trim()) ? v.trim() : null);
  const accentLight = pick(["primary", "accent_on_light", "secondary", "accent"], LIGHT);
  const reminder = candidates(["reminder"]).find((c) => contrast(INK, c) >= 4.5) ?? INPUTS.light.reminder;
  return {
    accentLight,
    accentDark: pick(["accent", "accent_on_dark", "secondary", "primary"], DARK) ?? (accentLight ? liftForDark(accentLight) : null),
    actionLight: pick(["action"], LIGHT) ?? accentLight,
    reminder,
    headingFont: font(fonts.headings),
    bodyFont: font(fonts.body),
  };
}


/**
 * White-labelling is a choice the business makes, not a side effect of having a brand kit: the app is Ogma unless the
 * settings say `app_theme: {white_label: true}`.
 */
export function whiteLabelOn(config: JsonObject | null | undefined): boolean {
  const t = config?.app_theme;
  return typeof t === "object" && t !== null && !Array.isArray(t) && (t as JsonObject).white_label === true;
}

export interface SpineBrand {
  /** The left-hand bar's colour, or null for Ogma's ink. */
  spine: string | null;
  /** Bone or ink, whichever reads on it. */
  onSpine: string;
  /** The logo file in the business's document storage that reads on that colour, if any. */
  logoPath: string | null;
}

/**
 * The white-label spine: app_theme.spine if given, else the brand's primary colour, as long as bone or ink reads on
 * it at 4.5:1. The logo is the brand's dark-background version on a dark bar (bone text), else the light one.
 */
export function spineBrand(config: JsonObject | null | undefined): SpineBrand {
  const none: SpineBrand = { spine: null, onSpine: onSpine(INPUTS.light.spine), logoPath: null };
  if (!whiteLabelOn(config)) return none;
  const theme = (config?.app_theme ?? {}) as JsonObject;
  const brand = (config?.brand ?? {}) as JsonObject;
  const colours = (brand.colours ?? {}) as JsonObject;
  const raw = [theme.spine, colours.primary, colours.secondary].find((v) => typeof v === "string" && parseHex(v));
  const spine = typeof raw === "string" ? norm(raw) : null;
  const text = spine ? onSpine(spine) : none.onSpine;
  if (spine && contrast(text, spine) < 4.5) return none;
  const logo = (brand.logo ?? {}) as JsonObject;
  const pick = (k: string) => (typeof logo[k] === "string" && (logo[k] as string).trim() ? (logo[k] as string).trim() : null);
  const logoPath = text === BONE ? (pick("dark") ?? pick("light")) : (pick("light") ?? pick("dark"));
  return { spine, onSpine: text, logoPath };
}

/** Applies white-labelling to the app: the business's colour on the left-hand bar, nothing else. */
export function applyBrand(config: JsonObject | null | undefined, doc: Document = document): SpineBrand {
  const b = spineBrand(config);
  const root = doc.documentElement;
  const set = (name: string, v: string | null) => (v ? root.style.setProperty(name, v) : root.style.removeProperty(name));
  set("--brand-spine", b.spine);
  set("--brand-on-spine", b.spine ? b.onSpine : null);
  // Ogma's active notch is seal red on ink; on the business's colour it is bone or ink, which always reads.
  set("--brand-spine-highlight", b.spine ? b.onSpine : null);
  // Earlier versions recoloured highlight and action; make sure nothing is left over.
  for (const n of ["highlight", "action", "on-action"]) for (const t of ["light", "dark"]) root.style.removeProperty(`--brand-${n}-${t}`);
  return b;
}
